'use client';

import { faBars, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buildLoginUrl } from '@pickleballinc/lib/session/login-url';
import { Application } from '@pickleballinc/lib/types';
import { Navbar, NavbarItem } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { NotificationsPopupMobile } from '../Notification/NotificationsPopupMobile';
import { useUser } from '../UserInfoProvider';
import { UserProfile } from '../UserProfile';
import { BLOG_LABEL, MEME_SLUG, MEME_SUBCATEGORY_SLUG } from '../utils/constants';
import { getNavItems, getSwitcherOptions } from '../utils/nav.config';
import { NewsCategoryShortInterface, NotificationLookup } from '../utils/types';

import GlobalSearch, { GlobalSearchButtonsResponse } from './GlobalSearch';
import { HeaderLogo } from './HeaderLogo';
import { HeaderSwitcher } from './HeaderSwitcher';
import { Nav } from './Nav';

interface MainHeaderProps {
	searchButtons: GlobalSearchButtonsResponse;
	application: Application;
	categories: NewsCategoryShortInterface[];
	notifications: NotificationLookup | undefined;
	canAccessManage: boolean;
	pickleballHref: string;
	tournamentsHref: string;
	clubsHref: string;
	teamLeaguesHref?: string;
	leaguesHref: string;
	aprHref?: string;
	ssoLink: string;
	checkoutLink: string;
	cdnUrl: string;
	isProd?: boolean;
	showAPRItem?: boolean;
	showPricing?: boolean;
}

function useCanHover() {
	const [canHover, setCanHover] = useState(false);

	useEffect(() => {
		const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
		setCanHover(mq.matches);

		const handler = (e: MediaQueryListEvent) => setCanHover(e.matches);
		mq.addEventListener('change', handler);

		return () => mq.removeEventListener('change', handler);
	}, []);

	return canHover;
}

const validBlogPathname = /^\/[a-zA-Z0-9-]+\/[a-zA-Z0-9-]+$/;

export function MainHeader({
	searchButtons,
	application,
	categories,
	notifications,
	canAccessManage,
	pickleballHref,
	tournamentsHref,
	clubsHref,
	teamLeaguesHref = '',
	leaguesHref,
	aprHref,
	ssoLink,
	checkoutLink,
	cdnUrl,
	showAPRItem,
	showPricing
}: MainHeaderProps) {
	const user = useUser();
	const [searchShown, setSearchShown] = useState(false);
	const pathname = usePathname();
	const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
	const searchRef = useRef(null);
	const [baseURL, setBaseURL] = useState('');
	const [isOpen, setIsOpen] = useState(false);

	const canHover = useCanHover();

	const toggleDropdown = () => setIsOpen(!isOpen);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const switcherRef = useRef<HTMLDivElement>(null);

	const closeDropdown = () => {
		timeoutRef.current = setTimeout(() => {
			setIsOpen(false);
		}, 200);
	};

	const openDropdown = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		setIsOpen(true);
	};

	const [headerHeight, setHeaderHeight] = useState(0);
	const headerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const updateHeight = () => {
			if (headerRef.current) {
				const headerRect = headerRef.current.getBoundingClientRect();
				setHeaderHeight(headerRect.bottom);
			}
		};

		updateHeight();

		window.addEventListener('resize', updateHeight);
		window.addEventListener('scroll', updateHeight, { passive: true });

		return () => {
			window.removeEventListener('resize', updateHeight);
			window.removeEventListener('scroll', updateHeight);
		};
	}, []);

	useEffect(() => {
		setBaseURL(window.location.href);
	}, []);

	const onRouteChangeStart = () => {
		setMobileMenuOpened(false);
	};

	useEffect(() => {
		onRouteChangeStart();
	}, [pathname]);

	useEffect(() => {
		if (mobileMenuOpened) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.removeProperty('overflow');
		}
	}, [mobileMenuOpened]);

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			if (switcherRef.current && e.target instanceof Node && !switcherRef.current.contains(e.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) {
			document.body.classList.add('pb-header-menu-open');
		} else {
			document.body.classList.remove('pb-header-menu-open');
		}

		return () => {
			document.body.classList.remove('pb-header-menu-open');
		};
	}, [isOpen]);

	useEffect(() => {
		if (mobileMenuOpened) {
			document.body.classList.add('pb-hamburger-menu-open');
		} else {
			document.body.classList.remove('pb-hamburger-menu-open');
		}

		return () => {
			document.body.classList.remove('pb-hamburger-menu-open');
		};
	}, [mobileMenuOpened]);

	const isTournament = application === 'tournament';
	const isClubs = application === 'clubs';
	const isTeamLeagues = application === 'team-leagues';
	const isAPR = application === 'apr-rankings';
	const isLeagues = application === 'leagues';
	const isPickleball = application === 'pickleball' || isAPR || isLeagues;

	const isLocalHost = isPickleball
		? pickleballHref?.includes('localhost')
		: isClubs
			? clubsHref?.includes('localhost')
			: isTeamLeagues
				? teamLeaguesHref?.includes('localhost')
				: isTournament
					? tournamentsHref?.includes('localhost')
					: false;
	// Log In: the local `/login` on localhost (with the path until hydration sets `baseURL`, so the
	// server HTML's link comes back here too), else SSO, which needs the full URL.
	const loginHref = buildLoginUrl({
		ssoUrl: ssoLink,
		isLocalhost: !!isLocalHost,
		continueTo: isLocalHost ? baseURL || pathname : baseURL
	});

	const pickleballPrefix = `http${isPickleball && isLocalHost ? '' : 's'}://`;

	const baseOrigin = isLocalHost
		? ''
		: baseURL.includes(`${leaguesHref}`)
			? `${leaguesHref}`
			: baseURL.includes(`${aprHref}`)
				? `${aprHref}`
				: baseURL.includes(`${clubsHref}`)
					? `${clubsHref}`
					: baseURL.includes(`${teamLeaguesHref}`)
						? `${teamLeaguesHref}`
						: baseURL.includes(`${tournamentsHref}`)
							? `${tournamentsHref}`
							: `https://${pickleballHref}`;

	const filteredCategories = categories && categories.length > 0 ? categories.filter((category) => category.slug !== 'news') : [];

	const pathSegments = pathname?.split('/').filter((segment) => segment) || [];
	const categorySlug = pathSegments[0];
	const isBlogPathname = pathname && validBlogPathname.test(pathname) && filteredCategories.some((category) => category.slug === categorySlug);
	const switcherOptions = getSwitcherOptions({
		pickleballHref: `${pickleballPrefix}${pickleballHref}`,
		tournamentsHref,
		clubsHref,
		teamLeaguesHref,
		leaguesHref: isLocalHost && isPickleball ? `${leaguesHref}/leagues` : leaguesHref,
		aprHref: aprHref ? (isLocalHost && isPickleball ? `${aprHref}/apr-rankings` : aprHref) : `${pickleballPrefix}${pickleballHref}/apr-rankings`,
		showAPRItem: showAPRItem || application === 'apr-rankings'
	});

	const navItems = useMemo(
		() =>
			getNavItems({
				isLocalHost,
				application,
				pickleballHref,
				tournamentsHref,
				clubsHref,
				teamLeaguesHref: teamLeaguesHref,
				leaguesHref,
				aprHref,
				user,
				showPricing
			}),
		[isLocalHost, application, pickleballHref, tournamentsHref, clubsHref, teamLeaguesHref, leaguesHref, aprHref]
	);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const togglePickleballHubDropdown = () => setIsDropdownOpen((prev) => !prev);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target;
			if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
				setIsDropdownOpen(false);
			}
		};

		if (isDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isDropdownOpen]);

	return (
		<header ref={headerRef} className="relative z-50" id="pb-mainheader">
			<div className="relative">
				{isOpen ? (
					<div
						className="fixed left-0 right-0 bottom-0 backdrop-blur-sm z-40"
						style={{ top: `${headerHeight}px` }}
						role="button"
						tabIndex={0}
						onClick={() => setIsOpen(false)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								setIsOpen(false);
							}
						}}
					/>
				) : null}
				<Navbar
					className={clsx(
						'sticky top-0 z-50 [&>*:first-child]:!max-w-[1280px]',
						isTournament
							? '!bg-[#114BBF]'
							: isTeamLeagues
								? '!bg-[#FF5005]'
								: isAPR
									? '!bg-[#3D820A]'
									: isClubs
										? '!bg-clubs-300'
										: isLeagues
											? '!bg-leagues-500'
											: '!bg-[#471CB1]'
					)}
					as="nav"
					role="navigation"
				>
					<div
						ref={switcherRef}
						className="static sm:relative"
						onMouseEnter={canHover ? openDropdown : undefined}
						onMouseLeave={canHover ? closeDropdown : undefined}
					>
						<Navbar.Brand
							onClick={toggleDropdown}
							className={clsx(
								'select-none !px-0 sm:!pl-4 !-skew-x-[15deg] size-full',
								isTournament && '!bg-blue-600 before:!bg-blue-600',
								isClubs && '!bg-clubs-700 before:!bg-clubs-700',
								isAPR && '!bg-[#4CA30D] before:!bg-[#4CA30D]',
								isLeagues && '!bg-leagues-300 before:!bg-leagues-300',
								isTeamLeagues && 'flex items-center justify-center !bg-[#EF612D] before:!bg-[#EF612D]'
							)}
						>
							<HeaderLogo
								switcherOptions={switcherOptions}
								application={application}
								cdnUrl={cdnUrl}
								isOpen={isOpen}
								setIsOpen={setIsOpen}
							/>
						</Navbar.Brand>
						{isOpen ? (
							<>
								<div
									className="absolute -translate-x-[171.5px] sm:left-6 sm:translate-x-0 h-[30px] w-[343px] cursor-default"
									onMouseEnter={() => setIsOpen(true)}
								/>
								<div className="absolute top-[74px] z-50 w-[343px] left-1/2 -translate-x-[171.5px] sm:left-6 sm:translate-x-0">
									<HeaderSwitcher
										switcherOptions={switcherOptions}
										application={application}
										cdnUrl={cdnUrl}
										isOpen={isOpen}
										headerHeight={headerHeight}
									/>
								</div>
							</>
						) : null}
					</div>
					<div className="mr-2 sm:mr-6" />

					<Nav
						baseOrigin={baseOrigin}
						application={application}
						pickleballHref={pickleballHref}
						aprHref={aprHref || ''}
						tournamentsHref={tournamentsHref}
						clubsHref={clubsHref}
						teamLeaguesHref={teamLeaguesHref}
						leaguesHref={leaguesHref}
						navItems={navItems}
					/>

					<div className="ml-auto mr-1 flex">
						{user?.uuid ? (
							<NavbarItem className="!inline-flex sm:!hidden">
								<UserProfile
									cdnUrl={cdnUrl}
									user={user}
									canAccessManage={canAccessManage}
									pickleballHref={pickleballHref}
									tournamentHref={tournamentsHref}
									leaguesHref={leaguesHref}
									clubsHref={clubsHref}
									teamLeaguesHref={teamLeaguesHref}
									ssoLink={ssoLink}
									isLocalhost={isLocalHost}
									pathname={pathname}
									isPickleball={isPickleball}
								/>
							</NavbarItem>
						) : (
							<NavbarItem className="!inline-flex sm:!hidden">
								<CustomLink href={loginHref} rel="nofollow">
									Login
								</CustomLink>
							</NavbarItem>
						)}

						<div
							className={clsx(
								'!hidden -skew-x-[15deg] items-center justify-center px-7 text-white sm:!inline-flex bg-experiment',
								isTournament && '!bg-blue-600',
								isClubs && '!bg-clubs-700',
								isAPR && '!bg-[#4CA30D]',
								isLeagues && '!bg-leagues-300',
								isTeamLeagues && '!bg-[#EF612D]'
							)}
							role="button"
							tabIndex={0}
							data-testid={`nav-pickleball-hub`}
						>
							<div className="skew-x-[15deg]">
								<div className="relative" ref={dropdownRef}>
									<div
										onClick={togglePickleballHubDropdown}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												togglePickleballHubDropdown();
											}
										}}
										role="button"
										tabIndex={0}
										className="flex cursor-pointer items-center gap-1.5 text-white"
									>
										<span className="text-sm font-semibold transition-all duration-150 hover:text-opacity-80">{BLOG_LABEL}</span>
										<FontAwesomeIcon icon={faChevronDown} size="sm" />
									</div>
									{isDropdownOpen ? (
										<div className="absolute mt-[25px] transform -translate-x-[26px] min-w-[172px] max-w-[172px] rounded-lg bg-white py-2 shadow-lg">
											{filteredCategories.map((category) => (
												<div
													key={category.uuid}
													className="cursor-pointer px-1.5 text-sm font-medium text-gray-900 hover:bg-[#EBE9FE]"
												>
													<CustomLink
														href={`${pickleballPrefix}${pickleballHref}/${category.slug}/${category.slug === MEME_SLUG ? `1?subcategory=${MEME_SUBCATEGORY_SLUG}` : 'all'}`}
														className={`block px-4 py-2 line-clamp-1 text-ellipsis ${isBlogPathname && categorySlug === category.slug ? 'underline' : ''}`}
													>
														{category.title}
													</CustomLink>
												</div>
											))}
										</div>
									) : null}
								</div>
							</div>
						</div>

						<NavbarItem
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								setSearchShown(!searchShown);
							}}
							className={clsx(
								'!px-3 sm:!px-4',
								isTournament && (!searchShown ? 'sm:!border-r-blue-600 sm:!border-l-transparent' : '!border-blue-600 !bg-blue-600'),
								isClubs && (!searchShown ? 'sm:!border-r-clubs-700 sm:!border-l-transparent' : '!border-clubs-700 !bg-clubs-700'),
								isAPR && (!searchShown ? 'sm:!border-r-[#4CA30D] sm:!border-l-transparent' : '!border-[#4CA30D] !bg-[#4CA30D]'),
								isLeagues &&
									(!searchShown ? 'sm:!border-r-leagues-300 sm:!border-l-transparent' : '!border-leagues-300 !bg-leagues-300'),
								isTeamLeagues &&
									(!searchShown ? 'sm:!border-r-[#EF612D] sm:!border-l-transparent' : '!border-[#EF612D] !bg-[#EF612D]'),
								!isTeamLeagues && !isLeagues && !isAPR && !isClubs && !isTournament && !searchShown && 'sm:!border-l-transparent'
							)}
							active={searchShown}
							ref={searchRef}
							tabIndex={0}
						>
							<FontAwesomeIcon
								className="sm:!hidden"
								icon={faSearch}
								aria-label="Search"
								title="Search Icon"
								role="button"
								titleId="search-icon-mobile"
							/>
							<FontAwesomeIcon
								className="!hidden sm:!flex"
								size="lg"
								icon={faSearch}
								aria-label="Search"
								title="Search Icon"
								role="button"
								titleId="search-icon-desktop"
							/>
						</NavbarItem>

						{user?.uuid ? (
							<div className="flex w-[41px] items-center justify-center sm:hidden">
								<div className="inline-flex items-start sm:hidden">
									<div className="h-5 w-px -skew-x-[15deg] bg-gray-700" />
								</div>
								<div className="inline-flex w-full items-center justify-center sm:hidden">
									<NotificationsPopupMobile notifications={notifications} checkoutLink={`${checkoutLink}/pending`} />
								</div>
							</div>
						) : null}

						<NavbarItem className="!inline-flex items-center !px-3 sm:!hidden" onClick={() => setMobileMenuOpened(!mobileMenuOpened)}>
							<FontAwesomeIcon icon={faBars} />
						</NavbarItem>
					</div>

					{mobileMenuOpened ? (
						<div className="fixed inset-0 flex w-full overflow-hidden sm:hidden">
							<div
								aria-hidden="true"
								className="absolute inset-0 bg-white/30 backdrop-blur-md"
								onClick={() => setMobileMenuOpened(false)}
							/>
							<div
								aria-hidden="true"
								className={clsx(
									'absolute right-2 top-2 flex size-11 items-center justify-center rounded-md',
									isTournament && 'bg-blue-500',
									isClubs && 'bg-clubs-700',
									isAPR && 'bg-[#4CA30D]',
									isLeagues && 'bg-leagues-300',
									isTeamLeagues && 'bg-[#EF612D]',
									!isTournament && !isClubs && !isAPR && !isLeagues && !isTeamLeagues && 'bg-experiment'
								)}
								onClick={() => setMobileMenuOpened(false)}
							>
								<FontAwesomeIcon icon={faTimes} className="text-white" />
							</div>
							<div className="relative z-50 w-[46%] shrink-0 overflow-auto bg-gray-800">
								<ul className="flex flex-col divide-y divide-gray-900">
									<li className="list-none bg-white">
										<CustomLink
											href={`${pickleballPrefix}${pickleballHref}/watch-now`}
											className="block px-6 py-4 text-sm font-bold leading-5 text-gray-900"
										>
											Watch
										</CustomLink>
									</li>
									{navItems.map((item) => (
										<Fragment key={item.id}>
											{item.children === BLOG_LABEL && filteredCategories ? (
												<>
													{filteredCategories.map((category) => (
														<li key={category.uuid} className="list-none">
															<CustomLink
																href={`${pickleballPrefix}${pickleballHref}/${category.slug}/all`}
																className="block px-6 py-4 text-sm font-bold leading-5 text-white"
															>
																{category.title}
															</CustomLink>
														</li>
													))}
												</>
											) : (
												<li className="list-none">
													<CustomLink href={item.href} className="block px-6 py-4 text-sm font-bold leading-5 text-white">
														{item.children}
													</CustomLink>
												</li>
											)}
										</Fragment>
									))}
								</ul>
							</div>
						</div>
					) : null}
				</Navbar>
			</div>
			{searchShown ? (
				<GlobalSearch
					searchVisible={searchShown}
					globalSearchButtons={searchButtons}
					toggleRef={searchRef}
					onHide={() => setSearchShown(false)}
					pickleballHref={pickleballHref}
					tournamentsHref={tournamentsHref}
					clubsHref={clubsHref}
					teamLeaguesHref={teamLeaguesHref}
					leaguesHref={leaguesHref}
					application={application}
				/>
			) : null}
		</header>
	);
}
